import { EmailAccountCard } from "~/react/components/email_accounts/email-account-card"
import type { ManualAccount } from "~/react/components/email_accounts/types"
import { validateManualAccount } from "~/react/components/email_accounts/validation"

type Props = {
  accounts: ManualAccount[]
  domains: string[]
  duplicateEmailMap: Record<string, number>
  onChange: (next: ManualAccount[]) => void
  signatureForNew: string
  className?: string
  showIncomplete: boolean
  currencyCode?: string
  emailPriceCents?: number
}

export default function ManualAccountsSection({
  accounts,
  domains,
  duplicateEmailMap,
  onChange,
  signatureForNew,
  className,
  showIncomplete: _showIncomplete,
  currencyCode,
  emailPriceCents,
}: Props) {
  const handleChange = (next: ManualAccount) => {
    const existingAccount = accounts.find((a) => a.id === next.id)
    if (!existingAccount) {
      return
    }

    const isComplete =
      (next.first || "").trim() &&
      (next.last || "").trim() &&
      (next.local || "").trim() &&
      (next.domain || "").trim()

    const nextWithStatus: ManualAccount = {
      ...next,
      status: isComplete ? "ready" : "draft",
    }

    const updated = accounts.map((a) => (a.id === next.id ? nextWithStatus : a))
    onChange(updated)
  }

  const handleRemove = (id: string) => {
    onChange(accounts.filter((a) => a.id !== id))
  }

  const isAccountInvalid = (acc: ManualAccount): boolean => {
    // Ignore completely blank account rows
    const isBlank =
      !(acc.first || "").trim() &&
      !(acc.last || "").trim() &&
      !(acc.local || "").trim() &&
      !(acc.domain || "").trim()
    if (isBlank) return false

    const isIncomplete =
      !acc.first.trim() || !acc.last.trim() || !acc.local.trim() || !acc.domain
    const emailKey = `${acc.local.trim().toLowerCase()}@${(acc.domain || "").trim().toLowerCase()}`
    const isDuplicate = duplicateEmailMap[emailKey] > 1
    return isIncomplete || isDuplicate
  }

  // Only show incomplete/draft manual accounts in the form.
  // All "ready" accounts (manual or auto) are represented in the preview list.
  const visibleAccounts = accounts.filter((acc) => acc.status !== "ready")

  return (
    <div className={className}>
      {visibleAccounts.map((acc, index) => {
        const emailKey = `${acc.local.trim().toLowerCase()}@${(acc.domain || "").trim().toLowerCase()}`
        const isDuplicate = duplicateEmailMap[emailKey] > 1
        const isInvalid = isAccountInvalid(acc)
        const validation = validateManualAccount(acc, {
          allowedDomains: (domains || []).map((d) => (d || "").toLowerCase()),
          allowGlobalSignature: true,
          globalSignature: signatureForNew,
        })
        const hasGlobalSignature = (signatureForNew || "").trim().length > 0
        return (
          <div key={acc.id} className={index > 0 ? "mt-2" : undefined}>
            <EmailAccountCard
              id={acc.id}
              mode="edit"
              account={{
                first: acc.first,
                last: acc.last,
                local: acc.local,
                domain: acc.domain,
                signature: acc.signature,
              }}
              allowedDomains={domains}
              duplicate={isDuplicate}
              invalid={isInvalid}
              hasGlobalSignature={hasGlobalSignature}
              externalErrors={validation.errors}
              open={
                !acc.local && !acc.domain && acc.open !== false
                  ? true
                  : acc.open
              }
              onOpenChange={(open) => handleChange({ ...acc, open })}
              onChange={(updated) =>
                handleChange({ ...acc, ...updated, open: acc.open })
              }
              onRemove={() => handleRemove(acc.id)}
              globalSignature={signatureForNew}
              currencyCode={currencyCode}
              emailPriceCents={emailPriceCents}
            />
          </div>
        )
      })}
    </div>
  )
}
